'use client';

import { c as _c } from "react/compiler-runtime";
import { z } from '@repo/schema';
import type { OrganisationScope } from '@repo/upfetch';
import { getScopeOrganisationId } from '@repo/upfetch/core';
import { createContext, type PropsWithChildren, useContext, useEffect, useState } from 'react';
import { clearOrganisationCookie, LAST_ORGANISATION_COOKIE_NAME, ORG_COOKIE_NAME, type OrganisationCookies, setOrganisationCookie } from './cookies.ts';

// Where both values lived before their cookies; adopted once, then removed (CK-2805 D10).
const LEGACY_SCOPE_STORAGE_KEY = 'org';
const LEGACY_LAST_ORGANISATION_STORAGE_KEY = 'last-org';
type OrganisationContextValue = {
  lastOrganisationId: number | undefined;
  rememberOrganisation: (organisationId: number) => void;
  scope: OrganisationScope;
  selectScope: (scope: OrganisationScope) => void;
};
const OrganisationContext = createContext<null | OrganisationContextValue>(null);
const writeScopeCookie = (scope: OrganisationScope) => {
  if (scope.kind === 'organisation') setOrganisationCookie(ORG_COOKIE_NAME, scope.id);else clearOrganisationCookie(ORG_COOKIE_NAME);
};

// Reads and removes a legacy localStorage value, so it is adopted at most once.
const takeLegacyOrganisationId = (key: string): number | undefined => {
  try {
    const stored = localStorage.getItem(key);
    localStorage.removeItem(key);
    if (!stored || stored === 'undefined') return undefined;
    const result = z.coerce.number().int().positive().safeParse(JSON.parse(stored));
    return result.success ? result.data : undefined;
  } catch (error) {
    console.error('Failed to adopt stored organisation:', error);
    return undefined;
  }
};
type OrganisationProviderProps = PropsWithChildren<{
  // Read from the request by the root layout, so the first render already knows both.
  cookies: OrganisationCookies;
}>;
export const OrganisationProvider = t0 => {
  const $ = _c(15);
  const {
    children,
    cookies
  } = t0;
  const [scope, setScope] = useState(cookies.scope);
  let t1;
  if ($[0] !== cookies.lastOrganisationId || $[1] !== cookies.scope) {
    t1 = cookies.lastOrganisationId ?? getScopeOrganisationId(cookies.scope);
    $[0] = cookies.lastOrganisationId;
    $[1] = cookies.scope;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [lastOrganisationId, setLastOrganisationId] = useState(t1);
  const hasScopeCookie = cookies.scope.kind === "organisation";
  const hasLastOrganisationCookie = cookies.lastOrganisationId !== undefined;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = organisationId => {
      setLastOrganisationId(organisationId);
      setOrganisationCookie(LAST_ORGANISATION_COOKIE_NAME, organisationId);
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const rememberOrganisation = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = nextScope => {
      setScope(nextScope);
      writeScopeCookie(nextScope);
      if (nextScope.kind === "organisation") {
        rememberOrganisation(nextScope.id);
      }
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const selectScope = t3;
  let t4;
  let t5;
  if ($[5] !== hasLastOrganisationCookie || $[6] !== hasScopeCookie) {
    t4 = () => {
      const legacyOrganisationId = takeLegacyOrganisationId(LEGACY_SCOPE_STORAGE_KEY);
      const legacyLastOrganisationId = takeLegacyOrganisationId(LEGACY_LAST_ORGANISATION_STORAGE_KEY);
      const adoptedScopeId = hasScopeCookie ? undefined : legacyOrganisationId;
      if (adoptedScopeId !== undefined) {
        const legacyScope = {
          id: adoptedScopeId,
          kind: "organisation"
        };
        setScope(legacyScope);
        writeScopeCookie(legacyScope);
      }
      const adoptedLastOrganisationId = legacyLastOrganisationId ?? adoptedScopeId;
      if (adoptedLastOrganisationId !== undefined && !hasLastOrganisationCookie) {
        setLastOrganisationId(adoptedLastOrganisationId);
        setOrganisationCookie(LAST_ORGANISATION_COOKIE_NAME, adoptedLastOrganisationId);
      }
    };
    t5 = [hasLastOrganisationCookie, hasScopeCookie];
    $[5] = hasLastOrganisationCookie;
    $[6] = hasScopeCookie;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  if ($[9] !== lastOrganisationId || $[10] !== scope) {
    t6 = {
      lastOrganisationId,
      rememberOrganisation,
      scope,
      selectScope
    };
    $[9] = lastOrganisationId;
    $[10] = scope;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== children || $[13] !== t6) {
    t7 = <OrganisationContext.Provider value={t6} data-sentry-element="OrganisationContext.Provider" data-sentry-component="OrganisationProvider" data-sentry-source-file="organisation-provider.tsx">{children}</OrganisationContext.Provider>;
    $[12] = children;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
};
const useOrganisationContext = hook => {
  const context = useContext(OrganisationContext);
  if (!context) {
    throw new Error(`${hook} must be used within an OrganisationProvider`);
  }
  return context;
};

/** The Organisation scope, and a way to select another one (or "All organisations"). */
export const useOrganisationScope = () => {
  const $ = _c(3);
  const {
    scope,
    selectScope
  } = useOrganisationContext("useOrganisationScope");
  let t0;
  if ($[0] !== scope || $[1] !== selectScope) {
    t0 = {
      scope,
      selectScope
    };
    $[0] = scope;
    $[1] = selectScope;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};

/** The Organisation this browser last used, and a way to remember another one. */
export const useLastOrganisation = () => {
  const $ = _c(3);
  const {
    lastOrganisationId,
    rememberOrganisation
  } = useOrganisationContext("useLastOrganisation");
  let t0;
  if ($[0] !== lastOrganisationId || $[1] !== rememberOrganisation) {
    t0 = {
      lastOrganisationId,
      rememberOrganisation
    };
    $[0] = lastOrganisationId;
    $[1] = rememberOrganisation;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};